import React, { useState, useEffect } from 'react';
import { TextField, Grid, Typography, Button, Box, Card, CardContent } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { styled, alpha } from '@mui/material/styles';
import Iconify from '../../../../components/Iconify';
import { dispatch } from '../../../../redux/store';
import {
  addToWatchListSuccess,
  getInstrumentList,
  getWatchList,
  startTradeLoading,
} from '../../../../redux/slices/trades';
import useAuth from '../../../../hooks/useAuth';
import modifyScriptName from '../../../../utils/modifyScriptName';

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  boxShadow: theme.shadows[0],
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const ScriptCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 48,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.background.neutral, 0.02),
  },
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderBottom: `2px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'light'
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.dark, 0.2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 42,
}));

const ExchangeHeader = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1),
  '&:last-child': { paddingBottom: theme.spacing(1) },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  minHeight: 48,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const segements = ['NSE', 'MCX', 'COMEX', 'CRYTPO', 'FOREX'];

function AddToWatchlist() {
  const [searchText, setSearchText] = useState('');
  const [filteredScripts, setFilteredScripts] = useState([]);
  const tradeData = useSelector((state) => state?.trade);
  const { watchlist, instrumentData, isLoading } = tradeData;
  const [selectedExchange, setSelectedExchange] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const scripts = instrumentData?.docs || [];
  const userSegements = segements.filter((segement) => {
    if (user[segement]) return user[segement].allow;
    return false;
  });

  useEffect(() => {
    dispatch(getInstrumentList());
    dispatch(getWatchList(false));
  }, []);

  useEffect(() => {
    filterScripts();
  }, [searchText, watchlist, instrumentData]);

  const filterScripts = () => {
    const allScripts = watchlist ? [].concat(...Object.values(watchlist)) : [];
    const filtered = searchText
      ? scripts.filter(
        (script) =>
          script.tradingsymbol.toLowerCase().includes(searchText.toLowerCase()) &&
          !allScripts.some((s) => s?._id === script._id) &&
          userSegements.includes(script?.exchange?.name)
      )
      : scripts.filter(
        (script) => !allScripts.some((s) => s?._id === script._id) && userSegements.includes(script?.exchange?.name)
      );
    setFilteredScripts(filtered);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleBackclick = () => {
    // navigate(PATH_DASHBOARD.bankerRole.watchlist.root);
    navigate(-1);
  };

  const handleExchangeClick = (exchange) => {
    setSelectedExchange(selectedExchange === exchange ? null : exchange);
  };

  // const handleAddToWatchlist = (script) => {
  //   console.log('script', script);
  //   dispatch(startTradeLoading());
  //   const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || {};
  //   updatedWatchlist[script.exchange.name] = updatedWatchlist[script.exchange.name] || [];
  //   updatedWatchlist[script.exchange.name].push(script);
  //   localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
  //   dispatch(addToWatchListSuccess(updatedWatchlist));
  //   const updatedFilteredScripts = filteredScripts.filter((s) => s._id !== script._id);
  //   setFilteredScripts(updatedFilteredScripts);
  // };
  const handleAddToWatchlist = (script) => {
    dispatch(startTradeLoading());

    // Get the current watchlist from local storage
    const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || {};

    // Initialize the exchange watchlist if it doesn't exist
    updatedWatchlist[script.exchange.name] = updatedWatchlist[script.exchange.name] || [];

    // Create a set of current script IDs for the exchange
    const scriptIds = new Set(updatedWatchlist[script.exchange.name].map((s) => s._id));

    // Add the script if its _id is not already in the set
    if (!scriptIds.has(script._id)) {
      updatedWatchlist[script.exchange.name].push(script);
      localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
      dispatch(addToWatchListSuccess(updatedWatchlist));
    }

    // Update the filtered scripts
    const updatedFilteredScripts = filteredScripts.filter((s) => s._id !== script._id);
    setFilteredScripts(updatedFilteredScripts);
  };

  const renderRow = ({ index, style }) => {
    const script = filteredScripts[index];
    return (
      <Grid
        container
        style={style}
        key={script._id}
        alignItems="center"
        spacing={2}
        sx={{ cursor: 'pointer', ':hover': { backgroundColor: 'action.selected' } }}
        className="list-item"
        onClick={() => handleAddToWatchlist(script)}
      >
        <Grid item xs={8}>
          <Typography>{modifyScriptName(script.tradingsymbol, script.expiry)}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">{script.exchange.name}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Box textAlign="right">
            <LoadingButton variant="contained" onClick={() => handleAddToWatchlist(script)}>
              Add
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    );
  };
  return (
    <Box sx={{ p: 1.5 }}>
      <Button
        startIcon={<Iconify icon={'eva:arrow-back-outline'} sx={{ width: 18, height: 18 }} />}
        variant="outlined"
        sx={{ 
          mb: 2,
          py: 0.5,
          fontSize: 13,
          minHeight: 36,
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
          }
        }}
        onClick={handleBackclick}
      >
        Back
      </Button>

      <Grid container spacing={2}>
        <Grid item xs={12}>          <TextField
            fullWidth
            placeholder="Search Scripts..."
            value={searchText}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <Iconify
                  icon="eva:search-fill"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: 1,
                    color: 'text.disabled',
                  }}
                />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 36,
                borderRadius: 1,
                backgroundColor: 'background.neutral',
                transition: 'all 0.2s',
                '& fieldset': {
                  borderColor: 'transparent',
                  borderWidth: 1,
                },
                '&:hover': {
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: 'primary.lighter',
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: 'background.paper',
                  boxShadow: (theme) => `0 0 0 2px \${theme.palette.primary.lighter}`,
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 1,
                  },
                },
              },
              '& .MuiInputBase-input': {
                fontSize: 13,
                '&::placeholder': {
                  color: 'text.disabled',
                  opacity: 1,
                },
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          {searchText ? (
            <StyledCard>
              <StyledHeader>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Search Results
                </Typography>
              </StyledHeader>              <List
                height={360}
                itemCount={filteredScripts.length}
                itemSize={48}
                width="100%"
              >
                {({ index, style }) => {
                  const script = filteredScripts[index];
                  return (
                    <ScriptCard style={style}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}                        noWrap
                      >
                        {modifyScriptName(script.tradingsymbol, script.expiry)}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            fontSize: 11,
                          }}
                        >
                          {script.exchange.name}
                        </Typography>
                      </Typography>

                      <LoadingButton
                        size="small"
                        variant="contained"
                        onClick={() => handleAddToWatchlist(script)}
                        loading={isLoading}
                        sx={{
                          minWidth: 72,
                          fontSize: 12,
                          fontWeight: 600,
                          py: 0.5,
                        }}
                      >
                        Add
                      </LoadingButton>
                    </ScriptCard>
                  );
                }}
              </List>
            </StyledCard>
          ) : (
            <Grid container spacing={2}>
              {segements
                .filter((segment) => userSegements.includes(segment))
                .map((exchange) => (
                  <Grid item xs={12} key={exchange}>
                    <StyledCard>
                      <ExchangeHeader onClick={() => handleExchangeClick(exchange)}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {exchange}
                        </Typography>
                        <ExpandMore sx={{
                          transform: selectedExchange === exchange ? 'rotate(180deg)' : 'none',
                          transition: (theme) => theme.transitions.create('transform'),
                          color: 'text.secondary',
                        }} />
                      </ExchangeHeader>

                      {selectedExchange === exchange && (                        <List
                          height={280}
                          itemCount={filteredScripts.filter(script => script.exchange.name === exchange).length}
                          itemSize={48}
                          width="100%"
                        >
                          {({ index, style }) => {
                            const script = filteredScripts.filter(s => s.exchange.name === exchange)[index];
                            return (
                              <ScriptCard style={style}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                  }}                                  noWrap
                                >
                                  {modifyScriptName(script.tradingsymbol, script.expiry)}
                                </Typography>

                                <LoadingButton
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddToWatchlist(script)}
                                  loading={isLoading}
                                  sx={{
                                    minWidth: 72,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    py: 0.5,
                                  }}
                                >
                                  Add
                                </LoadingButton>
                              </ScriptCard>
                            );
                          }}
                        </List>
                      )}
                    </StyledCard>
                  </Grid>
                ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default AddToWatchlist;
